import React, { useEffect, useRef } from 'react';

/**
 * Mobile-optimized modal component
 * - Full-screen on mobile with bottom sheet style
 * - Standard modal on desktop
 * - Swipe-to-dismiss support
 * - Sticky header and footer
 */
const MobileModal = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    footer,
    size = 'default' // 'default', 'large', 'full'
}) => {
    const modalRef = useRef(null);
    const contentRef = useRef(null);
    const startY = useRef(0);
    const currentY = useRef(0);
    
    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
        
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [isOpen]);
    
    // Handle swipe to dismiss on mobile
    const handleTouchStart = (e) => {
        if (window.innerWidth > 767) return; // Desktop - no swipe
        startY.current = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
        if (window.innerWidth > 767) return;
        if (!contentRef.current) return;
        
        // Only allow swipe down if scrolled to top
        if (contentRef.current.scrollTop > 0) return;
        
        currentY.current = e.touches[0].clientY;
        const diff = currentY.current - startY.current;
        
        if (diff > 0) { // Swiping down
            e.preventDefault();
            if (modalRef.current) {
                modalRef.current.style.transform = `translateY(${diff}px)`;
                modalRef.current.style.transition = 'none';
            }
        }
    };
    
    const handleTouchEnd = () => {
        if (window.innerWidth > 767) return;
        
        const diff = currentY.current - startY.current;
        
        if (modalRef.current) {
            if (diff > 150) { // Swipe down threshold
                onClose();
            } else {
                modalRef.current.style.transform = '';
                modalRef.current.style.transition = 'transform 0.3s ease-out';
            }
        }
        
        startY.current = 0;
        currentY.current = 0;
    };
    
    if (!isOpen) return null;
    
    const sizeClasses = {
        'default': 'max-w-2xl',
        'large': 'max-w-4xl',
        'full': 'max-w-full'
    };
    
    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div 
                ref={modalRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`relative w-full ${sizeClasses[size]} bg-white shadow-2xl
                    md:rounded-lg
                    rounded-t-2xl md:rounded-b-lg
                    max-h-[95vh] md:max-h-[90vh]
                    flex flex-col
                    transform transition-transform duration-300 ease-out`}
                style={{ maxHeight: 'calc(100vh - 2rem)' }}
            >
                {/* Swipe Handle (mobile only) */}
                <div className="md:hidden flex justify-center pt-2 pb-1">
                    <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
                </div>
                
                {/* Header - Sticky */}
                <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 flex items-center justify-between rounded-t-2xl md:rounded-t-lg">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Close"
                    >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                {/* Content - Scrollable */}
                <div 
                    ref={contentRef}
                    className="flex-1 overflow-y-auto p-6"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    {children}
                </div>
                
                {/* Footer - Sticky (if provided) */}
                {footer && (
                    <div className="sticky bottom-0 bg-white z-10 px-6 py-4 border-t border-gray-200">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileModal;

